/**
 * Capitalize the first letter of each word in a string
 * @param {string} str - Input string
 * @returns {string} Formatted string
 */
export const capitalizeFirstLetterOfEachWord = (str) => {
  if (!str) return "";
  return str
    .replace(/[-_]/g, " ")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

/**
 * Convert string to uppercase, replacing underscores with spaces
 * @param {string} str - Input string
 * @returns {string} Uppercase string
 */
export const convertToUpperCase = (str) => {
  if (!str) return "";
  return str.replace(/_/g, " ").toUpperCase();
};

/**
 * Format date string to locale format
 * @param {string} dateString - Date string to format
 * @returns {string} Formatted date or "N/A"
 */
export const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "N/A";
  if (date.getFullYear() === 1) {
    return "";
  }
  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

export const buildTransactionDocumentFileName = (headDetails = {}) => {
  const transactionNumber = String(headDetails.tran_no || "")
    .trim()
    .replace(/[<>:"/\\|?*]/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
  if (!transactionNumber) return "";

  const dateValue =
    headDetails.date ||
    headDetails.tran_date ||
    headDetails.transaction_date ||
    headDetails.invoice_date ||
    headDetails.created_at;
  if (!dateValue) return transactionNumber;

  const dateString = String(dateValue).trim();
  const isoDate = dateString.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
  const dayFirstDate = dateString.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
  let formattedDate = "";

  if (isoDate) {
    formattedDate = `${isoDate[1]}-${isoDate[2].padStart(2, "0")}-${isoDate[3].padStart(2, "0")}`;
  } else if (dayFirstDate) {
    formattedDate = `${dayFirstDate[3]}-${dayFirstDate[2].padStart(2, "0")}-${dayFirstDate[1].padStart(2, "0")}`;
  } else {
    const date = new Date(dateValue);
    if (!Number.isNaN(date.getTime())) {
      formattedDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
    }
  }

  return [transactionNumber, formattedDate].filter(Boolean).join("-");
};

/**
 * Clean report name by removing common suffixes
 * @param {string} reportName - Report name to clean
 * @returns {string} Cleaned report name
 */
export const cleanReportName = (reportName) => {
  if (!reportName) return "";
  return reportName.replace(/\s+(register|report)$/i, "");
};

/**
 * Truncate text with ellipsis
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length
 * @returns {string} Truncated text
 */
export const truncateText = (text, maxLength = 50) => {
  if (!text || text.length <= maxLength) return text || "";
  return `${text.substring(0, maxLength)}...`;
};

/**
 * Format currency value
 * @param {number} value - Value to format
 * @param {string} currency - Currency code (default: INR)
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (value, currency = "INR") => {
  if (value == null || isNaN(value)) return "-";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  }).format(value);
};

/**
 * Format number with decimal places
 * @param {number} value - Value to format
 * @param {number} decimals - Number of decimal places
 * @returns {string} Formatted number
 */
export const formatNumber = (value, decimals = 2) => {
  if (value == null || isNaN(value)) return "-";
  return Number(value).toFixed(decimals);
};

export const convertAmountToWords = (amount, converter) => {
  const numericAmount = Number(amount);
  if (!Number.isFinite(numericAmount)) return "";

  const [wholeRupees, paise] = Math.abs(numericAmount)
    .toFixed(2)
    .split(".")
    .map(Number);
  const rupeeWords = `${numericAmount < 0 ? "Minus " : ""}${converter.convert(wholeRupees)} Rupee${wholeRupees === 1 ? "" : "s"}`;

  return paise ? `${rupeeWords} and ${converter.convert(paise)} Paise` : rupeeWords;
};

const URL_PATTERN = /^https?:\/\//i;

const urlToFileName = (url) => {
  const tail = url.split("?")[0].split("#")[0].split("/").pop() || url;
  try {
    return decodeURIComponent(tail);
  } catch (_) {
    return tail;
  }
};

/**
 * Converts file URL value(s) to their file names; non-URL values are returned unchanged.
 */
export const formatFileUrlValue = (value) => {
  let list = value;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed.startsWith("[")) {
      try {
        list = JSON.parse(trimmed);
      } catch (_) {
        list = value;
      }
    }
  }

  if (Array.isArray(list)) {
    if (
      !list.length ||
      !list.every((v) => typeof v === "string" && URL_PATTERN.test(v.trim()))
    ) {
      return value;
    }
    return list.map((v) => urlToFileName(v.trim())).join(", ");
  }

  if (typeof list !== "string") return value;
  const parts = list
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);
  if (!parts.length || !parts.every((p) => URL_PATTERN.test(p))) return value;
  return parts.map(urlToFileName).join(", ");
};
